import * as d3 from 'd3';
import React from 'react';

import * as browser from 'lib/Browser';
import TimeEditableClass from 'notebook/protocols/editor/TimeEditable';
import TimeDisplay from 'notebook/protocols/viewer/TimeDisplay';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ElementMouseEvent} from 'util/Types';

import {EditHelpers} from '../timers/TimerConstants';

export interface TimerDisplayProps {
  inlineCopy?: boolean;
  inProgress: boolean;
  msLeft: number;
  msTotal: number;
  resetTimer: (e: ElementMouseEvent) => void;
  startTimer: (e: ElementMouseEvent) => void;
  stopTimer: (e: ElementMouseEvent) => void;
  toggleStart: (e: ElementMouseEvent) => void;
  allowEdits?: boolean;
  editHelpers?: EditHelpers;
}

interface State {
  editing: boolean;
}

class TimerDisplay extends React.Component<TimerDisplayProps, State> {
  override state: State = {
    editing: false,
  };

  editTimer = (): void => {
    this.setState({editing: true}, () => {
      this.props.editHelpers!.setSelection!({
        start: 6,
        end: 6,
        cid: TimeEditableClass.computeCid(this.props.editHelpers!.cid),
      });
    });
  };

  finalizeTimer = (): void => {
    this.setState({editing: false});
  };

  renderProgress = (): JSX.Element => {
    const startAngle = Math.PI * 2 * (1 - this.props.msLeft / this.props.msTotal);
    return (
      <div className="time-progress">
        {(() => {
          if (browser.isBrowser) {
            // Only render SVG if looking from a browser. The loss of a circle next to the timer is
            // a pretty minimal cost towards avoiding d3 (which doesn't work without DOM).
            const arc = d3
              .arc()
              .innerRadius(44)
              .outerRadius(50)
              .startAngle(startAngle)
              .endAngle(() => Math.PI * 2);
            return (
              <svg viewBox="0 0 100 100">
                <path d={arc.toString()} transform="translate(50, 50)" />
              </svg>
            );
          }
          return null;
        })()}
      </div>
    );
  };

  override render(): JSX.Element {
    return (
      <div className="step-timer">
        <TimeDisplay
          editing={this.state.editing}
          editHelpers={this.props.editHelpers}
          msLeft={this.props.msLeft}
          msTotal={this.props.msTotal}
          toggleStart={this.props.toggleStart}
          finalizeTimer={this.finalizeTimer}
          renderProgress={this.renderProgress}
        />
        <div className="time-controls">
          {this.props.inProgress ? (
            <Button
              buttonStyle={ButtonStyle.NEGATIVE}
              size={ButtonSize.SMALL}
              text="Stop"
              onClick={this.props.stopTimer}
            />
          ) : (
            [
              this.props.msLeft < this.props.msTotal ? (
                <Button
                  key="reset"
                  buttonStyle={ButtonStyle.SECONDARY}
                  size={ButtonSize.SMALL}
                  icon="refresh"
                  onClick={this.props.resetTimer}
                />
              ) : undefined,
              this.props.msLeft > 0 ? (
                <Button
                  key="start"
                  buttonStyle={ButtonStyle.PRIMARY}
                  size={ButtonSize.SMALL}
                  text="Start"
                  onClick={this.props.startTimer}
                />
              ) : undefined,
              this.props.allowEdits ? (
                <Button
                  key="edit"
                  buttonStyle={ButtonStyle.SECONDARY}
                  size={ButtonSize.SMALL}
                  text="Edit"
                  onClick={this.editTimer}
                />
              ) : undefined,
            ]
          )}
        </div>
      </div>
    );
  }
}

export default TimerDisplay;
